import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Users, 
  Search,
  Filter,
  MoreVertical,
  Check,
  X
} from 'lucide-react';
import { projectAPI, userAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const ProjectManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showAssignManagerModal, setShowAssignManagerModal] = useState(false);
  const [showAssignAdminModal, setShowAssignAdminModal] = useState(false);
  const [showAssignSuperAdminModal, setShowAssignSuperAdminModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedManager, setSelectedManager] = useState('');
  const [selectedAdmin, setSelectedAdmin] = useState('');
  const [selectedSuperAdmin, setSelectedSuperAdmin] = useState('');
  const [projectDetails, setProjectDetails] = useState(null);
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // Wait 500ms after user stops typing

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  const { data: projects, isLoading } = useQuery(
    ['admin-projects', debouncedSearchTerm],
    () => projectAPI.getAllProjects({ search: debouncedSearchTerm }),
    {
      select: (response) => response.data.projects,
    }
  );

  const { data: employees } = useQuery(
    'all-employees',
    () => {
      console.log('User role:', user?.role);
      // Temporarily force admin endpoint to debug 403 issue
      console.log('Calling getEmployees (Admin) - forced for debugging');
      return userAPI.getEmployees();
    },
    {
      enabled: !!user, // Only run when user is loaded
        select: (response) => {
          console.log('ProjectManagement - API Response:', response);
          // Handle different response structures
          if (response?.data?.data) {
            return response.data.data; // For admin endpoint with {data: [...], total: ...}
          } else if (response?.data) {
            return response.data; // For super admin endpoint with direct array
          } else {
            return []; // Fallback to empty array
          }
        },
      onError: (error) => {
        console.error('Error fetching employees:', error);
        toast.error('Failed to fetch employees: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  const { data: managers } = useQuery(
    'all-managers',
    () => {
      // Use appropriate endpoint based on user role
      if (user?.role === 'SUPER_ADMIN') {
        return userAPI.getAllManagers();
      } else {
        // Admin uses admin endpoint
        return userAPI.getAllManagersAdmin();
      }
    },
    {
      enabled: !!user,
      select: (response) => {
        if (response?.data?.data) {
          return response.data.data;
        } else if (response?.data) {
          return response.data;
        } else {
          return [];
        }
      },
      onError: (error) => {
        console.error('Error fetching managers:', error);
        toast.error('Failed to fetch managers: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  // Fetch admins (only for SUPER_ADMIN)
  const { data: admins } = useQuery(
    'all-admins',
    () => userAPI.getAllAdmins(),
    {
      enabled: !!user && user?.role === 'SUPER_ADMIN',
      select: (response) => {
        if (response?.data?.data) {
          return response.data.data;
        } else if (response?.data) {
          return response.data;
        } else {
          return [];
        }
      },
      onError: (error) => {
        console.error('Error fetching admins:', error);
        toast.error('Failed to fetch admins: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  // Fetch super admins (only for SUPER_ADMIN)
  const { data: superAdmins } = useQuery(
    'all-super-admins',
    () => userAPI.getAllSuperAdmins(),
    {
      enabled: !!user && user?.role === 'SUPER_ADMIN',
      select: (response) => {
        // Backend returns: { users: [...], total: ... }
        if (response?.data?.users) {
          return response.data.users;
        } else if (response?.data?.data) {
          return response.data.data;
        } else if (Array.isArray(response?.data)) {
          return response.data;
        } else {
          return [];
        }
      },
      onError: (error) => {
        console.error('Error fetching super admins:', error);
        toast.error('Failed to fetch super admins: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  // Fetch detailed project information when a project is selected
  const { data: detailedProject, isLoading: loadingDetails } = useQuery(
    ['project-details', selectedProject?.id],
    () => projectAPI.getProjectById(selectedProject.id),
    {
      enabled: !!selectedProject?.id,
      select: (response) => response.data.project,
    }
  );

  const createMutation = useMutation(
    (projectData) => projectAPI.createProject(projectData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-projects');
        setShowCreateModal(false);
        toast.success('Project created successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create project');
      },
    }
  );

  const updateMutation = useMutation(
    ({ projectId, projectData }) => projectAPI.updateProject(projectId, projectData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-projects');
        setShowEditModal(false);
        setSelectedProject(null);
        toast.success('Project updated successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update project');
      },
    }
  );

  const deleteMutation = useMutation(
    (projectId) => projectAPI.deleteProject(projectId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-projects');
        toast.success('Project deleted successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete project');
      },
    }
  );

  const assignMutation = useMutation(
    ({ projectId, employeeId }) => projectAPI.assignEmployeeToProject(projectId, employeeId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-projects');
        queryClient.invalidateQueries(['project-details', selectedProject?.id]);
        if (showAssignModal) {
          setShowAssignModal(false);
          setSelectedProject(null);
          setSelectedEmployee('');
        }
        toast.success('Employee assigned to project successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to assign employee');
      },
    }
  );

  const removeAssignmentMutation = useMutation(
    ({ projectId, employeeId }) => projectAPI.removeEmployeeFromProject(projectId, employeeId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-projects');
        queryClient.invalidateQueries(['project-details', selectedProject?.id]);
        toast.success('Employee removed from project successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to remove employee');
      },
    }
  );

  const assignManagerMutation = useMutation(
    ({ projectId, managerId }) => projectAPI.assignManagerToProject(projectId, managerId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-projects');
        queryClient.invalidateQueries(['project-details', selectedProject?.id]);
        if (showAssignManagerModal) {
          setShowAssignManagerModal(false);
          setSelectedProject(null);
          setSelectedManager('');
        }
        toast.success('Manager assigned to project successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to assign manager');
      },
    }
  );

  const removeManagerMutation = useMutation(
    ({ projectId, managerId }) => projectAPI.removeManagerFromProject(projectId, managerId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-projects');
        queryClient.invalidateQueries(['project-details', selectedProject?.id]);
        toast.success('Manager removed from project successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to remove manager');
      },
    }
  );

  const assignAdminMutation = useMutation(
    ({ projectId, adminId }) => projectAPI.assignAdminToProject(projectId, adminId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-projects');
        queryClient.invalidateQueries(['project-details', selectedProject?.id]);
        if (showAssignAdminModal) {
          setShowAssignAdminModal(false);
        }
        toast.success('Admin assigned to project successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to assign admin');
      },
    }
  );

  const removeAdminMutation = useMutation(
    ({ projectId, adminId }) => projectAPI.removeAdminFromProject(projectId, adminId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-projects');
        queryClient.invalidateQueries(['project-details', selectedProject?.id]);
        toast.success('Admin removed from project successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to remove admin');
      },
    }
  );

  const assignSuperAdminMutation = useMutation(
    ({ projectId, superAdminId }) => projectAPI.assignSuperAdminToProject(projectId, superAdminId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-projects');
        queryClient.invalidateQueries(['project-details', selectedProject?.id]);
        if (showAssignSuperAdminModal) {
          setShowAssignSuperAdminModal(false);
        }
        toast.success('Super Admin assigned to project successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to assign super admin');
      },
    }
  );

  const removeSuperAdminMutation = useMutation(
    ({ projectId, superAdminId }) => projectAPI.removeSuperAdminFromProject(projectId, superAdminId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-projects');
        queryClient.invalidateQueries(['project-details', selectedProject?.id]);
        toast.success('Super Admin removed from project successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to remove super admin');
      },
    }
  );

  const handleCreateProject = (projectData) => {
    createMutation.mutate(projectData);
  };

  const handleUpdateProject = (projectData) => {
    updateMutation.mutate({ projectId: selectedProject.id, projectData });
  };

  const handleDeleteProject = (projectId) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      deleteMutation.mutate(projectId);
    }
  };

  const handleAssignEmployee = () => {
    if (selectedProject && selectedEmployee) {
      assignMutation.mutate({ projectId: selectedProject.id, employeeId: selectedEmployee });
    }
  };

  const handleRemoveEmployee = (projectId, employeeId) => {
    if (window.confirm('Are you sure you want to remove this employee from the project?')) {
      removeAssignmentMutation.mutate({ projectId, employeeId });
    }
  };

  const handleAssignManager = () => {
    if (selectedProject && selectedManager) {
      assignManagerMutation.mutate({ projectId: selectedProject.id, managerId: selectedManager });
    }
  };

  const handleRemoveManager = (projectId, managerId) => {
    if (window.confirm('Are you sure you want to remove this manager from the project?')) {
      removeManagerMutation.mutate({ projectId, managerId });
    }
  };

  const handleAssignAdmin = () => {
    if (selectedProject && selectedAdmin) {
      assignAdminMutation.mutate({ projectId: selectedProject.id, adminId: selectedAdmin });
    }
  };

  const handleRemoveAdmin = (projectId, adminId) => {
    if (window.confirm('Are you sure you want to remove this admin from the project?')) {
      removeAdminMutation.mutate({ projectId, adminId });
    }
  };

  const handleAssignSuperAdmin = () => {
    if (selectedProject && selectedSuperAdmin) {
      assignSuperAdminMutation.mutate({ projectId: selectedProject.id, superAdminId: selectedSuperAdmin });
    }
  };

  const handleRemoveSuperAdmin = (projectId, superAdminId) => {
    if (window.confirm('Are you sure you want to remove this super admin from the project?')) {
      removeSuperAdminMutation.mutate({ projectId, superAdminId });
    }
  };

  const openEditModal = (project) => {
    setSelectedProject(project);
    setShowEditModal(true);
  };

  const openAssignModal = (project) => {
    setSelectedProject(project);
    setShowAssignModal(true);
  };

  const openAssignManagerModal = (project) => {
    setSelectedProject(project);
    setShowAssignManagerModal(true);
  };

  const openAssignAdminModal = (project) => {
    setSelectedProject(project);
    setShowAssignAdminModal(true);
  };

  const openAssignSuperAdminModal = (project) => {
    setSelectedProject(project);
    setShowAssignSuperAdminModal(true);
  };

  const openManageModal = (project) => {
    setSelectedProject(project);
    setShowManageModal(true);
  };

  if (isLoading || !user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Project Management</h1>
          <p className="text-gray-600 mt-1">Manage projects and assign employees</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Create Project</span>
        </motion.button>
      </div>

      {/* Search and Filter */}
      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects?.map((project) => (
          <motion.div
            key={project.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">{project.name}</h3>
                {project.description && (
                  <p className="text-gray-600 text-sm line-clamp-2">{project.description}</p>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => openEditModal(project)}
                  className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                  title="Edit Project"
                >
                  <Edit className="w-4 h-4" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => handleDeleteProject(project.id)}
                  className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                  title="Delete Project"
                >
                  <Trash2 className="w-4 h-4" />
                </motion.button>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
              <div className="flex items-center space-x-1">
                <Users className="w-4 h-4" />
                <span>{project.assignedEmployeesCount || 0} employees</span>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                project.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {project.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>

            {/* Manager Information */}
            {project.manager && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-medium">
                      {project.manager.firstName?.charAt(0)}{project.manager.lastName?.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-900">
                      Manager: {project.manager.firstName} {project.manager.lastName}
                    </p>
                    <p className="text-xs text-blue-600">{project.manager.email}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => openManageModal(project)}
                className="px-4 py-2 text-sm bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center space-x-2"
                title="Manage Assignments"
              >
                <MoreVertical className="w-4 h-4" />
                <span>Manage</span>
              </motion.button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Create Project Modal */}
      {showCreateModal && (
        <CreateProjectModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateProject}
          isLoading={createMutation.isLoading}
        />
      )}

      {/* Edit Project Modal */}
      {showEditModal && selectedProject && (
        <EditProjectModal
          project={selectedProject}
          onClose={() => {
            setShowEditModal(false);
            setSelectedProject(null);
          }}
          onSubmit={handleUpdateProject}
          isLoading={updateMutation.isLoading}
        />
      )}

      {/* Assign Employee Modal */}
      {showAssignModal && selectedProject && (
        <AssignEmployeeModal
          project={selectedProject}
          employees={employees || []}
          selectedEmployee={selectedEmployee}
          onEmployeeSelect={setSelectedEmployee}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedProject(null);
            setSelectedEmployee('');
          }}
          onSubmit={handleAssignEmployee}
          isLoading={assignMutation.isLoading}
        />
      )}

      {/* Assign Manager Modal */}
      {showAssignManagerModal && selectedProject && (
        <AssignManagerModal
          project={selectedProject}
          managers={managers || []}
          selectedManager={selectedManager}
          onManagerSelect={setSelectedManager}
          onClose={() => {
            setShowAssignManagerModal(false);
            setSelectedProject(null);
            setSelectedManager('');
          }}
          onSubmit={handleAssignManager}
          isLoading={assignManagerMutation.isLoading}
        />
      )}

      {/* Assign Admin Modal */}
      {showAssignAdminModal && selectedProject && (
        <AssignAdminModal
          project={selectedProject}
          admins={admins || []}
          selectedAdmin={selectedAdmin}
          onAdminSelect={setSelectedAdmin}
          onClose={() => {
            setShowAssignAdminModal(false);
            setSelectedProject(null);
            setSelectedAdmin('');
          }}
          onSubmit={handleAssignAdmin}
          isLoading={assignAdminMutation.isLoading}
        />
      )}

      {/* Assign Super Admin Modal */}
      {showAssignSuperAdminModal && selectedProject && (
        <AssignSuperAdminModal
          project={selectedProject}
          superAdmins={superAdmins || []}
          selectedSuperAdmin={selectedSuperAdmin}
          onSuperAdminSelect={setSelectedSuperAdmin}
          onClose={() => {
            setShowAssignSuperAdminModal(false);
            setSelectedProject(null);
            setSelectedSuperAdmin('');
          }}
          onSubmit={handleAssignSuperAdmin}
          isLoading={assignSuperAdminMutation.isLoading}
        />
      )}

      {/* Manage Employees Modal */}
      {showManageModal && selectedProject && (
        <ManageEmployeesModal
          project={selectedProject}
          projectDetails={detailedProject}
          employees={employees || []}
          managers={managers || []}
          admins={admins || []}
          superAdmins={superAdmins || []}
          selectedEmployee={selectedEmployee}
          selectedManager={selectedManager}
          selectedAdmin={selectedAdmin}
          selectedSuperAdmin={selectedSuperAdmin}
          onEmployeeSelect={setSelectedEmployee}
          onManagerSelect={setSelectedManager}
          onAdminSelect={setSelectedAdmin}
          onSuperAdminSelect={setSelectedSuperAdmin}
          onAssignEmployee={handleAssignEmployee}
          onAssignManager={handleAssignManager}
          onAssignAdmin={handleAssignAdmin}
          onAssignSuperAdmin={handleAssignSuperAdmin}
          onClose={() => {
            setShowManageModal(false);
            setSelectedProject(null);
            setSelectedEmployee('');
            setSelectedManager('');
            setSelectedAdmin('');
            setSelectedSuperAdmin('');
          }}
          onRemoveEmployee={handleRemoveEmployee}
          onRemoveManager={handleRemoveManager}
          onRemoveAdmin={handleRemoveAdmin}
          onRemoveSuperAdmin={handleRemoveSuperAdmin}
          isLoading={removeAssignmentMutation.isLoading || removeManagerMutation.isLoading || removeAdminMutation.isLoading || removeSuperAdminMutation.isLoading || assignMutation.isLoading || assignManagerMutation.isLoading || assignAdminMutation.isLoading || assignSuperAdminMutation.isLoading}
        />
      )}
    </div>
  );
};

// Create Project Modal Component
const CreateProjectModal = ({ onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
      >
        <h2 className="text-xl font-bold text-gray-900 mb-4">Create New Project</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project Name</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">Active</label>
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Creating...' : 'Create Project'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Edit Project Modal Component
const EditProjectModal = ({ project, onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    name: project.name,
    description: project.description || '',
    isActive: project.isActive,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
      >
        <h2 className="text-xl font-bold text-gray-900 mb-4">Edit Project</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project Name</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">Active</label>
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Updating...' : 'Update Project'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Assign Employee Modal Component
const AssignEmployeeModal = ({ project, employees, selectedEmployee, onEmployeeSelect, onClose, onSubmit, isLoading }) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
      >
        <h2 className="text-xl font-bold text-gray-900 mb-4">Assign Employee to {project.name}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Employee</label>
            <select
              value={selectedEmployee}
              onChange={(e) => onEmployeeSelect(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Choose an employee...</option>
              {employees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.firstName} {employee.lastName} ({employee.email})
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !selectedEmployee}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Assigning...' : 'Assign Employee'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Assign Manager Modal Component
const AssignManagerModal = ({ project, managers, selectedManager, onManagerSelect, onClose, onSubmit, isLoading }) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
      >
        <h2 className="text-xl font-bold text-gray-900 mb-4">Assign Manager to {project.name}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Manager</label>
            <select
              value={selectedManager}
              onChange={(e) => onManagerSelect(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Choose a manager...</option>
              {managers.map((manager) => (
                <option key={manager.id} value={manager.id}>
                  {manager.firstName} {manager.lastName} ({manager.email})
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !selectedManager}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Assigning...' : 'Assign Manager'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Assign Admin Modal Component
const AssignAdminModal = ({ project, admins, selectedAdmin, onAdminSelect, onClose, onSubmit, isLoading }) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
      >
        <h2 className="text-xl font-bold text-gray-900 mb-4">Assign Admin to {project.name}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Admin</label>
            <select
              value={selectedAdmin}
              onChange={(e) => onAdminSelect(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">Choose an admin...</option>
              {admins.map((admin) => (
                <option key={admin.id} value={admin.id}>
                  {admin.firstName} {admin.lastName} ({admin.email})
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !selectedAdmin}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Assigning...' : 'Assign Admin'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Assign Super Admin Modal Component
const AssignSuperAdminModal = ({ project, superAdmins, selectedSuperAdmin, onSuperAdminSelect, onClose, onSubmit, isLoading }) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
      >
        <h2 className="text-xl font-bold text-gray-900 mb-4">Assign Super Admin to {project.name}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Super Admin</label>
            <select
              value={selectedSuperAdmin}
              onChange={(e) => onSuperAdminSelect(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="">Choose a super admin...</option>
              {superAdmins.map((superAdmin) => (
                <option key={superAdmin.id} value={superAdmin.id}>
                  {superAdmin.firstName} {superAdmin.lastName} ({superAdmin.email})
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !selectedSuperAdmin}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Assigning...' : 'Assign Super Admin'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Manage Employees Modal Component
const ManageEmployeesModal = ({ 
  project, 
  projectDetails, 
  employees,
  managers,
  admins,
  superAdmins,
  selectedEmployee,
  selectedManager,
  selectedAdmin,
  selectedSuperAdmin,
  onEmployeeSelect,
  onManagerSelect,
  onAdminSelect,
  onSuperAdminSelect,
  onAssignEmployee,
  onAssignManager,
  onAssignAdmin,
  onAssignSuperAdmin,
  onClose, 
  onRemoveEmployee, 
  onRemoveManager, 
  onRemoveAdmin, 
  onRemoveSuperAdmin, 
  isLoading 
}) => {
  const [activeTab, setActiveTab] = React.useState('employees');

  // Filter out already assigned users from dropdowns
  const assignedEmployeeIds = projectDetails?.assignedEmployees?.map(emp => emp.id) || [];
  const assignedManagerIds = projectDetails?.assignedManagers?.map(mgr => mgr.id) || [];
  const assignedAdminIds = projectDetails?.assignedAdmins?.map(admin => admin.id) || [];
  const assignedSuperAdminIds = projectDetails?.assignedSuperAdmins?.map(sa => sa.id) || [];

  const availableEmployees = employees?.filter(emp => !assignedEmployeeIds.includes(emp.id)) || [];
  const availableManagers = managers?.filter(mgr => !assignedManagerIds.includes(mgr.id)) || [];
  const availableAdmins = admins?.filter(admin => !assignedAdminIds.includes(admin.id)) || [];
  const availableSuperAdmins = superAdmins?.filter(sa => !assignedSuperAdminIds.includes(sa.id)) || [];

  if (!projectDetails) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4"
        >
          <div className="flex items-center justify-center h-32">
            <div className="spinner"></div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Manage Team - {project.name}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-4">
          <p className="text-gray-600 text-sm">
            {projectDetails.description || 'No description provided'}
          </p>
        </div>

        {/* Assignment Buttons */}
        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <p className="text-sm font-medium text-gray-700 mb-3">Assign to Project:</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Employee</label>
              <select
                value={selectedEmployee}
                onChange={(e) => onEmployeeSelect(e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Select...</option>
                {availableEmployees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.firstName} {emp.lastName}
                  </option>
                ))}
              </select>
              {selectedEmployee && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    onAssignEmployee();
                    onEmployeeSelect('');
                  }}
                  disabled={isLoading}
                  className="mt-1 w-full px-2 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  Assign
                </motion.button>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Manager</label>
              <select
                value={selectedManager}
                onChange={(e) => onManagerSelect(e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select...</option>
                {availableManagers.map((mgr) => (
                  <option key={mgr.id} value={mgr.id}>
                    {mgr.firstName} {mgr.lastName}
                  </option>
                ))}
              </select>
              {selectedManager && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    onAssignManager();
                    onManagerSelect('');
                  }}
                  disabled={isLoading}
                  className="mt-1 w-full px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  Assign
                </motion.button>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Admin</label>
              <select
                value={selectedAdmin}
                onChange={(e) => onAdminSelect(e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">Select...</option>
                {availableAdmins.map((admin) => (
                  <option key={admin.id} value={admin.id}>
                    {admin.firstName} {admin.lastName}
                  </option>
                ))}
              </select>
              {selectedAdmin && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    onAssignAdmin();
                    onAdminSelect('');
                  }}
                  disabled={isLoading}
                  className="mt-1 w-full px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors disabled:opacity-50"
                >
                  Assign
                </motion.button>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">S.Admin</label>
              <select
                value={selectedSuperAdmin}
                onChange={(e) => onSuperAdminSelect(e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="">Select...</option>
                {availableSuperAdmins.map((sa) => (
                  <option key={sa.id} value={sa.id}>
                    {sa.firstName} {sa.lastName}
                  </option>
                ))}
              </select>
              {selectedSuperAdmin && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    onAssignSuperAdmin();
                    onSuperAdminSelect('');
                  }}
                  disabled={isLoading}
                  className="mt-1 w-full px-2 py-1 text-xs bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors disabled:opacity-50"
                >
                  Assign
                </motion.button>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-4">
          <button
            onClick={() => setActiveTab('employees')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'employees'
                ? 'border-b-2 border-primary-600 text-primary-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Employees ({projectDetails.assignedEmployees?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('managers')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'managers'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Managers ({projectDetails.assignedManagers?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('admins')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'admins'
                ? 'border-b-2 border-purple-600 text-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Admins ({projectDetails.assignedAdmins?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('superAdmins')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'superAdmins'
                ? 'border-b-2 border-orange-600 text-orange-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Super Admins ({projectDetails.assignedSuperAdmins?.length || 0})
          </button>
        </div>

        {/* Employees Tab */}
        {activeTab === 'employees' && (
          <div className="mb-4">
            {projectDetails.assignedEmployees && projectDetails.assignedEmployees.length > 0 ? (
              <div className="space-y-2">
                {projectDetails.assignedEmployees.map((employee) => (
                  <div
                    key={employee.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                        <span className="text-primary-700 font-medium text-sm">
                          {employee.firstName?.charAt(0)}{employee.lastName?.charAt(0)}
                        </span>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {employee.firstName} {employee.lastName}
                        </p>
                        <p className="text-sm text-gray-500">{employee.email}</p>
                        {employee.assignedByManagerName && (
                          <div className="mt-1 flex items-center space-x-1">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-primary-100 text-primary-800">
                              👤 Assigned by: {employee.assignedByManagerName}
                            </span>
                          </div>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          Assigned: {new Date(employee.assignedAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => onRemoveEmployee(project.id, employee.id)}
                      disabled={isLoading}
                      className="px-3 py-1 text-sm text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 flex items-center space-x-1"
                    >
                      <X className="w-4 h-4" />
                      <span>Remove</span>
                    </motion.button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No employees assigned to this project</p>
              </div>
            )}
          </div>
        )}

        {/* Managers Tab */}
        {activeTab === 'managers' && (
          <div className="mb-4">
            {projectDetails.assignedManagers && projectDetails.assignedManagers.length > 0 ? (
              <div className="space-y-2">
                {projectDetails.assignedManagers.map((manager) => (
                  <div
                    key={manager.id}
                    className="flex items-center justify-between p-3 bg-blue-50 rounded-lg"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-medium text-sm">
                          {manager.firstName?.charAt(0)}{manager.lastName?.charAt(0)}
                        </span>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {manager.firstName} {manager.lastName}
                        </p>
                        <p className="text-sm text-gray-500">{manager.email}</p>
                        <span className="inline-flex items-center px-2 py-0.5 mt-1 rounded text-xs font-semibold bg-blue-100 text-blue-800">
                          Manager Role
                        </span>
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => onRemoveManager(project.id, manager.id)}
                      disabled={isLoading}
                      className="px-3 py-1 text-sm text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 flex items-center space-x-1"
                    >
                      <X className="w-4 h-4" />
                      <span>Remove</span>
                    </motion.button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No managers assigned to this project</p>
              </div>
            )}
          </div>
        )}

        {/* Admins Tab */}
        {activeTab === 'admins' && (
          <div className="mb-4">
            {projectDetails.assignedAdmins && projectDetails.assignedAdmins.length > 0 ? (
              <div className="space-y-2">
                {projectDetails.assignedAdmins.map((admin) => (
                  <div
                    key={admin.id}
                    className="flex items-center justify-between p-3 bg-purple-50 rounded-lg"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-medium text-sm">
                          {admin.firstName?.charAt(0)}{admin.lastName?.charAt(0)}
                        </span>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {admin.firstName} {admin.lastName}
                        </p>
                        <p className="text-sm text-gray-500">{admin.email}</p>
                        <span className="inline-flex items-center px-2 py-0.5 mt-1 rounded text-xs font-semibold bg-purple-100 text-purple-800">
                          Admin Role
                        </span>
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => onRemoveAdmin(project.id, admin.id)}
                      disabled={isLoading}
                      className="px-3 py-1 text-sm text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 flex items-center space-x-1"
                    >
                      <X className="w-4 h-4" />
                      <span>Remove</span>
                    </motion.button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No admins assigned to this project</p>
              </div>
            )}
          </div>
        )}

        {/* Super Admins Tab */}
        {activeTab === 'superAdmins' && (
          <div className="mb-4">
            {projectDetails.assignedSuperAdmins && projectDetails.assignedSuperAdmins.length > 0 ? (
              <div className="space-y-2">
                {projectDetails.assignedSuperAdmins.map((superAdmin) => (
                  <div
                    key={superAdmin.id}
                    className="flex items-center justify-between p-3 bg-orange-50 rounded-lg"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-medium text-sm">
                          {superAdmin.firstName?.charAt(0)}{superAdmin.lastName?.charAt(0)}
                        </span>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {superAdmin.firstName} {superAdmin.lastName}
                        </p>
                        <p className="text-sm text-gray-500">{superAdmin.email}</p>
                        <span className="inline-flex items-center px-2 py-0.5 mt-1 rounded text-xs font-semibold bg-orange-100 text-orange-800">
                          Super Admin Role
                        </span>
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => onRemoveSuperAdmin(project.id, superAdmin.id)}
                      disabled={isLoading}
                      className="px-3 py-1 text-sm text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 flex items-center space-x-1"
                    >
                      <X className="w-4 h-4" />
                      <span>Remove</span>
                    </motion.button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No super admins assigned to this project</p>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ProjectManagement;
